// src/opencomponents/Register.js — V18 registration over the flesh corridor.
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { authApiClient } from '../api/apiClient';
import AuthStage from '../components/AuthStage';
import { Field, Input, PrimaryButton, ErrorText, TextLink } from '../components/ui';
import { color } from '../theme/tokens';

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const Terms = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-height: 44px;
    font-size: 13px;
    line-height: 1.8;
    color: ${color.text2};
    input { margin-top: 5px; accent-color: ${color.blood}; width: 16px; height: 16px; flex-shrink: 0; }
    a { color: ${color.bloodHi}; }
`;

const Foot = styled.p`
    margin: 0;
    text-align: center;
    font-size: 14px;
    line-height: 1.8;
    color: ${color.text3};
`;

const Success = styled.p`
    margin: 0 0 12px;
    font-size: 15px;
    line-height: 1.9;
    color: ${color.text};
`;

function Register() {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [countdown, setCountdown] = useState(5); 
    const navigate = useNavigate();

    useEffect(() => {
        let timer;
        if (successMessage && countdown > 0) {
            timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
        } 
        else if (countdown === 0) {
            navigate('/login');
        }
        return () => clearTimeout(timer);
    }, [successMessage, countdown, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!agreedToTerms) {
            setError('请先阅读并同意服务条款与隐私政策。');
            return;
        }
        if (password !== confirmPassword) {
            setError('两次输入的密码不一致。');
            return;
        }
        if (password.length < 6) {
           setError('密码至少 6 位。');
           return;
        }
        if (!email) {
            setError('请填写邮箱。');
            return;
        }

        setLoading(true);

        try {
            const response = await authApiClient.post('/auth/register-general', {
                username,
                email,
                password
            });
            setSuccessMessage(response.data.message || '注册成功，激活邮件已发送。');
        } catch (err) {
            const message = err.response?.data?.message || '注册失败，请重试。';
            setError(message);
        } finally {
            setLoading(false);
        }
    };


    return (
        <AuthStage title="登録" quote="僕は終ノ空に至るんだ">
            {(eye) => (successMessage ? (
                <>
                    <Success role="status">{successMessage}</Success>
                    <Foot>{countdown} 秒后前往 <TextLink to="/login">登录</TextLink></Foot>
                </>
            ) : (
                <Form onSubmit={handleSubmit}>
                    <Field>
                        <label htmlFor="reg-username">用户名</label>
                        <Input id="reg-username" type="text" autoComplete="username" value={username}
                            onChange={(e) => setUsername(e.target.value)} onFocus={eye.focusName} onBlur={eye.blur}
                            required disabled={loading} />
                    </Field>
                    <Field>
                        <label htmlFor="reg-email">邮箱</label>
                        <Input id="reg-email" type="email" autoComplete="email" value={email}
                            onChange={(e) => setEmail(e.target.value)} onFocus={eye.focusName} onBlur={eye.blur}
                            required disabled={loading} />
                    </Field>
                    <Field>
                        <label htmlFor="reg-password">密码（至少 6 位）</label>
                        <Input id="reg-password" type="password" autoComplete="new-password" value={password}
                            onChange={(e) => setPassword(e.target.value)} onFocus={eye.focusSecret} onBlur={eye.blur}
                            required disabled={loading} />
                    </Field>
                    <Field>
                        <label htmlFor="reg-confirm">确认密码</label>
                        <Input id="reg-confirm" type="password" autoComplete="new-password" value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)} onFocus={eye.focusSecret} onBlur={eye.blur}
                            required disabled={loading} />
                    </Field>
                    <Terms>
                        <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} disabled={loading} />
                        <span>我已阅读并同意 <Link to="/terms" target="_blank">服务条款</Link> 与 <Link to="/privacy" target="_blank">隐私政策</Link></span>
                    </Terms>
                    <ErrorText role="alert">{error}</ErrorText>
                    <PrimaryButton type="submit" disabled={loading || !agreedToTerms} $block>
                        {loading ? '提交中…' : '注 册'}
                    </PrimaryButton>
                    <Foot>已有账号？<TextLink to="/login">登录</TextLink></Foot>
                </Form>
            ))}
        </AuthStage>
    );
}

export default Register;
